// Testimonials block — a GlideJS slider with arrows only (no pager dots).
//
// Only mounted when there is more than one testimonial: with a single quote the
// same markup renders as a static block, so nothing needs to slide.
//
// No-ops when no testimonial sliders are on the page.

import Glide from "@glidejs/glide";

// --- Per-line highlight bars ------------------------------------------------
//
// The design sets each LINE of a quote in its own dark bar, with the bars
// varying in length and angle so the stack reads as hand-made marker strokes
// rather than a machine-set rectangle.
//
// CSS alone cannot do this. `box-decoration-break: clone` gives one box per
// line, but every box gets identical padding and no rotation — `transform`
// does not apply to the line fragments of an inline. So the text is measured
// after layout, split at its REAL wrap points, and each line re-wrapped in its
// own element that CSS can then pad and rotate individually.
//
// Splitting is word-by-word using getClientRects(): a Range over each word
// reports its position, and a jump in `top` means the browser wrapped there.
// This reads the actual layout, so it stays correct whatever the font,
// container width or user text turns out to be.

// Fixed per-line variation, cycled through in order. Deliberately not random:
// the bars must not reshuffle on every resize or slide change, and a designed
// rhythm beats a random one that occasionally lines everything up straight.
const LINE_STYLES = [
  { tilt: "-0.6deg", padLeft: "0.5em", padRight: "0.36em" },
  { tilt: "0.45deg", padLeft: "0.34em", padRight: "0.56em" },
  { tilt: "-0.35deg", padLeft: "0.6em", padRight: "0.4em" },
  { tilt: "0.7deg", padLeft: "0.38em", padRight: "0.34em" },
  { tilt: "-0.5deg", padLeft: "0.42em", padRight: "0.62em" },
];

function splitQuoteIntoLines(paragraph) {
  // The untouched text is kept on the element so a re-split (on resize, where
  // the wrap points move) starts from the original rather than from markup
  // this function already produced.
  if (paragraph.dataset.quoteText === undefined) {
    paragraph.dataset.quoteText = paragraph.textContent.trim();
  }

  const text = paragraph.dataset.quoteText;
  if (!text) return;

  // Reset to a single text node so the measuring pass below sees the browser's
  // natural wrapping, not last run's one-line-per-block layout.
  paragraph.classList.remove("block__testimonials__text--split");
  paragraph.textContent = text;

  const textNode = paragraph.firstChild;
  if (!textNode) return;

  // Walk the words, recording where the browser moved to a new line.
  const lines = [];
  let currentLine = "";
  let previousTop = null;
  let offset = 0;
  const range = document.createRange();

  for (const word of text.split(/\s+/)) {
    if (!word) continue;

    const start = text.indexOf(word, offset);
    if (start === -1) continue;
    offset = start + word.length;

    range.setStart(textNode, start);
    range.setEnd(textNode, offset);

    const rect = range.getClientRects()[0];
    if (!rect) {
      currentLine = currentLine ? `${currentLine} ${word}` : word;
      continue;
    }

    // A word whose top differs from the previous one has wrapped. The 2px
    // tolerance absorbs sub-pixel differences between words on the same line
    // (accents and descenders can shift a rect slightly).
    if (previousTop !== null && Math.abs(rect.top - previousTop) > 2) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = currentLine ? `${currentLine} ${word}` : word;
    }

    previousTop = rect.top;
  }

  if (currentLine) lines.push(currentLine);
  range.detach?.();

  // A single line needs no splitting — the fallback span already handles it.
  if (lines.length === 0) return;

  const fragment = document.createDocumentFragment();

  lines.forEach((line, index) => {
    const span = document.createElement("span");
    span.className = "block__testimonials__line";

    const style = LINE_STYLES[index % LINE_STYLES.length];
    span.style.setProperty("--tilt", style.tilt);
    span.style.setProperty("--pad-left", style.padLeft);
    span.style.setProperty("--pad-right", style.padRight);

    span.textContent = line;
    fragment.appendChild(span);
  });

  paragraph.textContent = "";
  paragraph.appendChild(fragment);
  paragraph.classList.add("block__testimonials__text--split");
}

// Publishes the quote's vertical midpoint so the arrows can centre on IT rather
// than on the whole slide — the slide also holds the attribution and the
// case-study button, which would drag a plain 50% well below the quote.
//
// Measured rather than hard-coded because the quote's height depends on how
// many lines it wraps to, which varies per testimonial and per viewport.
function positionArrows(slider) {
  const quote = slider.querySelector(".block__testimonials__text");
  if (!quote) return;

  const sliderRect = slider.getBoundingClientRect();
  const quoteRect = quote.getBoundingClientRect();
  if (!sliderRect.height || !quoteRect.height) return;

  const center = quoteRect.top - sliderRect.top + quoteRect.height / 2;
  slider.style.setProperty("--quote-center", `${center}px`);
}

function initQuoteLines() {
  const paragraphs = document.querySelectorAll(".block__testimonials__text");
  if (!paragraphs.length) return;

  const sliders = document.querySelectorAll("[data-testimonials]");

  const splitAll = () => {
    paragraphs.forEach(splitQuoteIntoLines);
    // After the split, since re-wrapping the lines changes the quote's height.
    sliders.forEach(positionArrows);
  };

  // Fonts change the metrics, so wrap points measured before Anton loads would
  // be wrong. Waiting for the font avoids a visible re-flow of the bars.
  if (document.fonts?.ready) {
    document.fonts.ready.then(splitAll);
  } else {
    splitAll();
  }

  // Wrap points move with the container width, so the lines are recomputed on
  // resize. Debounced — this measures layout and would otherwise run on every
  // frame of a drag.
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(splitAll, 200);
  });
}

export function initTestimonials() {
  initQuoteLines();

  document.querySelectorAll("[data-testimonials]").forEach((slider) => {
    if (slider.dataset.testimonialsReady === "true") return;

    const slides = slider.querySelectorAll(".glide__slide");
    if (slides.length <= 1) return;

    slider.dataset.testimonialsReady = "true";

    const glide = new Glide(slider, {
      type: "slider",
      // rewind so the arrows loop rather than dead-ending at either end —
      // there is no pager here to show position, so a hard stop reads as broken.
      rewind: true,
      perView: 1,
      gap: 0,
      animationDuration: 500,
    });

    // Glide sizes the track on mount and on resize; both change where the
    // quote sits, so the arrows are re-centred after each.
    glide.on(["mount.after", "resize"], () => positionArrows(slider));

    glide.mount();
  });
}
